export default class GalleryPolicy {
  constructor(gallery) {
    this.gallery = gallery;
    this.relatedTermsCount =
      this.gallery.relatedTerms?.length ||
      this.gallery.relatedTermsViewModel?.length ||
      0;
  }

  showGallery() {
    return !this.gallery.showEmptyResults();
  }

  showGroupEventToggle() {
    const { assettype } = this.gallery.searchParameters;
    return this.gallery.counts?.eventGroups?.[`${assettype}s`] > 0;
  }

  showAgreementsFilter() {
    return (
      !this.gallery.isRecentlyViewedImages() &&
      !this.gallery.isCuratedSet() &&
      !this.gallery.isStackSearch() &&
      this.gallery.searchProfile.hasAgreements &&
      !this.gallery.searchProfile.isUltraPackOnly &&
      !this.gallery.searchProfile.isPaPreview
    );
  }

  showAgreementsBanner() {
    return this.gallery.hasResults();
  }

  showFilterPanel() {
    return (
      !this.gallery.isStackSearch() &&
      !this.gallery.isCuratedSet() &&
      !this.gallery.isRecentlyViewedImages() &&
      !this.gallery.hasDownloadSelection &&
      !(this.gallery.isSearchByImage() && this.gallery.isFilm())
    );
  }

  showGallerySeoHeadline() {
    return (
      this.gallery.searchProfile.botOrFirstTimeVisitor &&
      this.gallery.hasResults() &&
      !this.gallery.isRecentlyViewedImages() &&
      !this.gallery.isArtist()
    );
  }

  showClarifications() {
    return (
      this.gallery.isCreativeImageOnly() &&
      this.gallery.clarifications &&
      this.gallery.clarifications.length
    );
  }

  showRelatedTerms() {
    return this.gallery.hasResults() && this.relatedTermsCount > 0;
  }

  showRelatedTermsInGallery(minRelatedTerms, relatedTermsIndex) {
    return (
      !this.gallery.hideRelatedTermsInGallery &&
      this.relatedTermsCount >= minRelatedTerms &&
      this.gallery.assets.length > relatedTermsIndex
    );
  }

  showGalleryHeadline() {
    return this.gallery.isIstock()
      ? this.gallery.hasResults() && !this.gallery.isSearchByImage()
      : !this.gallery.isAssetIdSearch() ||
          this.gallery.isEditorialOnly() ||
          this.gallery.isEvents() ||
          this.gallery.isMoreLikeThis();
  }

  showGalleryTitle() {
    return (
      this.gallery.isArtist() ||
      this.gallery.isEvents() ||
      this.gallery.isCuratedSet() ||
      this.gallery.isStackSearch() ||
      this.gallery.isRecentlyViewedImages()
    );
  }

  showGalleryByLine() {
    return this.gallery.isArtist() && this.gallery.isEvents();
  }

  showCorrectionsFamily() {
    return (
      this.gallery.queryCorrections?.family && !this.gallery.isAssetIdSearch()
    );
  }

  showGalleryHeaderTabs() {
    return (
      !this.gallery.isPortfolio() &&
      !this.gallery.isRecentlyViewedImages() &&
      !this.gallery.isVideoTemplate()
    );
  }

  showAssetTypeToggle() {
    return this.gallery.isSearchByImage() && !this.gallery.isEditorialImage();
  }

  showZSRCategories() {
    return (
      !this.gallery.totalResults > 0 &&
      this.gallery.searchProfile.hasZSRCategories
    );
  }

  showHistogramSearch() {
    return (
      this.gallery.isImage() &&
      this.gallery.isCreative() &&
      !this.gallery.isEditorial()
    );
  }

  showSimpleLayout() {
    return (
      this.gallery.isRecentlyViewedImages() ||
      this.gallery.isCuratedSet() ||
      this.gallery.isStackSearch() ||
      this.gallery.hasDownloadSelection ||
      this.gallery.renderGatewayView ||
      this.gallery.showEmptyResults()
    );
  }

  showHorizontalFilters() {
    return !this.showSimpleLayout();
  }

  showColorMoods() {
    return !!this.gallery.availableSearchParameters.mood;
  }

  showPagination() {
    return this.showGallery();
  }

  showAIModsMerchandisingTile(isMosaicLayout) {
    if (!isMosaicLayout) return false;
    if (this.gallery.isSubscriptionRepackagingActive()) return false;
    return this.showGenerativeAIMerchandising();
  }

  // Single Seat tile audience: like showGenerativeAIMerchandising but intentionally
  // WITHOUT the !isEditorial() exclusion, so the tile also reaches editorial-only and
  // mixed SRPs.
  showSingleSeatMerchandisingTile(isMosaicLayout) {
    if (!isMosaicLayout) return false;
    if (this.gallery.isSubscriptionRepackagingActive()) return false;
    return (
      (this.gallery.isImage() || this.gallery.isImageAndFilm()) &&
      !this.gallery.isFilm() &&
      !this.gallery.isIllustration()
    );
  }

  // SRP-footer merchandising banner (MerchandisingAdapter), which promotes generative-AI products.
  showGenerativeAIMerchandising() {
    return (
      (this.gallery.isImage() || this.gallery.isImageAndFilm()) &&
      !this.gallery.isFilm() &&
      !this.gallery.isEditorial() &&
      !this.gallery.isIllustration()
    );
  }

  showSignature(asset) {
    return asset.isIstockSignature;
  }

  // eslint-disable-next-line complexity
  showNLSSortByMessaging() {
    const isNLSEligibleAssetType =
      this.gallery.isEditorialImageOnly() || this.gallery.isEditorialFilm();

    return (
      isNLSEligibleAssetType &&
      this.gallery.searchProfile.hasNLSKeywordInterleave &&
      !this.gallery.isGroupedByEvent() &&
      !this.gallery.isEvents() &&
      !this.gallery.isSpecialSearch() &&
      !this.gallery.isEditorialArchival()
    );
  }

  showNLSKeywordInterleaveMessaging(hasNLSKeywordInterleaveForMostPopular) {
    if (
      !this.gallery.hasResults() ||
      !this.showNLSSortByMessaging() ||
      !this.gallery.isEnhancedSearch()
    ) {
      return false;
    }

    if (this.gallery.isSortedMostPopular()) {
      return hasNLSKeywordInterleaveForMostPopular;
    }

    return (
      this.gallery.isSortedBestMatch() ||
      this.gallery.isSortedNewest() ||
      this.gallery.isSortedOldest()
    );
  }

  showEnhancedSearchNotice() {
    return (
      this.gallery.isCreativeNLSAvailable() &&
      (this.gallery.isEnhancedSearch() || this.gallery.isPhraseProcessingOff())
    );
  }

  showPortfolioBanner() {
    return this.gallery.isPortfolio();
  }

  showLanguageCorrection() {
    const correction = this.gallery.queryCorrections?.language;
    return (
      correction &&
      (!correction.preview?.translations ||
        !(correction.preview?.preview_thumbs.length > 0) ||
        !correction.preview?.url)
    );
  }

  showPhraseCorrection() {
    return !!this.gallery.queryCorrections?.phrase;
  }

  showSortByDropdown() {
    return !!this.gallery.availableSearchParameters.sortorder;
  }

  showBestMatchSuggestionBanner() {
    return (
      this.gallery.searchProfile.hasNLSKeywordInterleave &&
      this.gallery.isEditorialImageOnly() &&
      !this.gallery.isSpecialSearch() &&
      this.gallery.pageNumber() === 1 &&
      !this.gallery.isSortedBestMatch() &&
      this.gallery.totalResults === 0
    );
  }
}
